<template>
  <div class="flex gap-2 align-items-start">
    <FindUser
      type="group"
      :fromUserHasGroups="componentOrigin == 'user-has-groups'"
      @add-user="handleAddGroup"
      @search-users="handleSearchGroups"
      :canManageUsers="canManageUsers"
      class="flex-1"
    />
    <div v-if="canManageUsers">
      <Button
        icon="pi pi-ellipsis-v"
        severity="secondary"
        @click="toggleMenu"
        aria-haspopup="true"
        aria-controls="groups-menu"
        v-tooltip.top="'Más opciones'"
      />
      <Menu
        ref="menu"
        id="groups-menu"
        :model="menuItems"
        :popup="true"
      />
    </div>
  </div>

  <div class="overflow-x-auto mt-3">
    <DataTable
      :value="groupsArray"
      :rowClass="rowClass"
      removableSort
      :loading="loading"
      loading-icon="pi pi-spin pi-spinner"
      scrollable
      :scrollHeight="totalHeight"
    >
      <template #empty>
        <div class="p-text-center">
          <span class="p-text-bold">No hay grupos</span>
        </div>
      </template>
      <Column header="Nombre" field="nombre" />
      <Column v-if="showAdminColumn" field="gestionaUsuarios">
        <template #header>
          <div
            @click="sortBy('gestionaUsuarios')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            Administrador
          </div>
        </template>

        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.gestionaUsuarios"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateGroup(slotProps.data, 'gestionaUsuarios')"
          />
        </template>
      </Column>
      <Column
        v-if="componentOrigin == 'report-has-users'"
        field="habilitado"
        header="Activo"
      >
        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.habilitado"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateGroup(slotProps.data, 'habilitado')"
          />
        </template>
      </Column>
      <Column field="actualizaArchivos" header="Archivos" v-if="showAdminColumn">
        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.actualizaArchivos"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateGroup(slotProps.data, 'actualizaArchivos')"
          />
        </template>
      </Column>
      <Column v-if="tieneRoles" field="role" header="Rol">
        <template #body="slotProps">
          <MultiSelect
            v-model="slotProps.data.roles"
            :options="Roles"
            optionValue="idRolInforme"
            optionLabel="nombre"
            placeholder="Selecciona un rol"
            class="rol-multiselect"
            filter
            @change="handleUpdateGroup(slotProps.data, 'Roles')"
            :showToggleAll="!rolesSelectorLoading.includes(slotProps.data.id)"
            :loading="rolesSelectorLoading.includes(slotProps.data.id)"
            :disabled="rolesSelectorLoading.includes(slotProps.data.id)"
          />
        </template>
      </Column>
      <Column header="Tipo">
        <template #body="slotProps">
          {{ getGroupType(slotProps.data) }}
        </template>
      </Column>
      <Column v-if="componentOrigin == 'report-has-users'" header="Usuarios">
        <template #body="slotProps">
          <span
            v-if="slotProps.data.usuarios?.length > 0"
            :class="
              showListUsersButton(slotProps.data)
                ? 'text-blue-200 cursor-pointer text-primary font-bold'
                : ''
            "
            @click="
              () => {
                if (showListUsersButton(slotProps.data))
                  showGroupHasUsersModal(slotProps.data);
              }
            "
            >{{ slotProps.data.usuarios?.length }}</span
          >
          <span v-else>0</span>
        </template>
      </Column>
      <Column
        v-if="userRol == 'Admin' || componentOrigin == 'report-has-users'"
      >
        <template #body="slotProps">
          <Button
            @click="deleteGroup(slotProps.data)"
            :icon="
              deletingGroup ==
              (originIsReport ? slotProps.data.id : slotProps.data.idGrupo)
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-trash'
            "
            severity="danger"
          />
        </template>
      </Column>
    </DataTable>
  </div>
  <Paginator
    :rows="rows"
    :totalRecords="totalResults"
    :rowsPerPageOptions="rowsPerPageOptions"
    @page="onPage"
  ></Paginator>

  <!-- User group has users modal -->
  <UserGroupHasUsersDialogView
    ref="userGroupHasUsersModalRef"
    @handleUpdateTableList="onPage()"
  />
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from "vue";
import Button from "primevue/button";
import Menu from "primevue/menu";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Paginator from "primevue/paginator";
import InputSwitch from "primevue/inputswitch";
import MultiSelect from "primevue/multiselect";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useAuthorization } from "@sttedra_ui/framework";

import UserGroupHasUsersDialogView from "@/pages/_root/user-group-has-users-dialog/UserGroupHasUsersDialogView.vue"; // eslint-disable-line
import FindUser from "@/components/partials/FindUser.vue";
import {
  appUserGroupTypes,
  appUserGroupAddUser,
  appUserGroupDeleteUser,
  appUserGroupListByUser,
} from "@/api/users-groups/users-groups.api";
import {
  appUserReportDetailsGroup,
  userReportAddGroup,
  userReportDeleteGroup,
  appUserReportUpdateUserGroup,
} from "@/api/reports/reports.api";
import { tipoClonacion } from "@/composables/_commons/enums.js";

// #region Props, Emits
const emit = defineEmits(["add-group", "delete-group", "copy"]);
const props = defineProps({
  componentOrigin: {
    type: String,
    required: true,
    // Valores posibles: ["report-has-users", "user-has-groups"]
  },
  informe: {
    type: Object,
    required: true,
  },
  user: {
    type: Object,
    required: true,
  },
  tieneRoles: {
    type: Boolean,
    required: true,
  },
  Roles: {
    type: Array,
    required: true,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const loading = ref(false);
const toast = useToast();
const groupsArray = ref([]);
const confirm = useConfirm();
const types = appUserGroupTypes();
const originIsReport = props.componentOrigin == "report-has-users";
const originIsUser = props.componentOrigin == "user-has-groups";
const totalHeight = ref("100vh");
const addGroup = ref(null);
const deletingGroup = ref(null);
const selectedImpersonation = ref(null);
const userGroupHasUsersModalRef = ref(null);
const rolesSelectorLoading = ref([]);
const useLocalUserGroups = ref(STTEDRA_USER_GROUPS_LOCAL);
const useADUserGroups = ref(STTEDRA_USER_GROUPS_AD);
const useUPRUCUserGroups = ref(STTEDRA_USER_GROUPS_UPRUC);

// Menu ref and items
const menu = ref(null);
const menuItems = ref([
  {
    label: 'Importar grupos de otro informe',
    icon: 'pi pi-copy',
    command: () => {
      openCopyUsersAndGroupsModal(tipoClonacion.Groups);
    }
  }
]);

const canManageUsers = computed(() => {
  return (
    props.informe?.gestionaUsuarios ||
    (selectedImpersonation.value
      ? selectedImpersonation.value.esAdministrador
      : userRol === "Admin")
  );
});

const sortField = ref(null);
const sortOrder = ref(null);
const pageIndex = ref(1);
const pageSize = ref(10);
const rows = ref(10);
const rowsPerPageOptions = ref([10, 20, 50]);
const totalResults = ref(0);
const search = ref("");

const showAdminColumn = computed(() => {
  if (!originIsReport) {
    return false;
  }

  let show = false;

  if (userRol == "Admin") {
    show = true;
  }

  if (selectedImpersonation.value) {
    show = selectedImpersonation.value.esAdministrador;
  }

  return show;
});
// #endregion

// #region Functions
function toggleMenu(event) {
  menu.value.toggle(event);
}

function showListUsersButton(group) {
  if (selectedImpersonation.value) {
    if (selectedImpersonation.value.esAdministrador) {
      return true;
    }
  } else {
    if (userRol == "Admin") {
      return true;
    }
  }

  if (
    group.tipo === 0 &&
    (`${useLocalUserGroups.value}` == "true" ||
      useLocalUserGroups.value == "true")
  ) {
    return true;
  }

  if (
    group.tipo === 1 &&
    (`${useADUserGroups.value}` == "true" || useADUserGroups.value == "true")
  ) {
    return true;
  }

  if (
    group.tipo === 2 &&
    (`${useUPRUCUserGroups.value}` == "true" ||
      useUPRUCUserGroups.value == "true")
  ) {
    return true;
  }

  return false;
}

async function handleAddGroup(params) {
  let response = null;

  if (originIsReport) {
    addGroup.value = {
      idInforme: props.informe.idInforme,
      tipo: params.groupType,
      actualizaArchivos: false,
      gestionaUsuarios: false,
    };

    switch (params.groupType) {
      case 0:
        addGroup.value.idGrupo = params.group.idGrupo;
        break;

      case 1:
        addGroup.value.IdAdGroup = params.group.id;
        addGroup.value.nombre = params.group.displayName;
        break;

      case 2:
        addGroup.value.IdUprucGroup = params.group.id;
        addGroup.value.nombre = params.group.nombre;
        break;
    }

    response = await userReportAddGroup(addGroup.value);
  } else {
    addGroup.value = {
      email: props.user.email,
      idGrupo: params.group.idGrupo,
    };

    response = await appUserGroupAddUser(addGroup.value);
  }

  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: "Grupo añadido",
      detail: "El grupo ha sido añadido correctamente",
      life: 3000,
    });

    emit("add-group");
    onPage();
  } else {
    const error = response.response?.data?.errors?.[0]?.message;

    toast.add({
      severity: "error",
      summary: "Error",
      detail: error,
      life: 3000,
    });
  }
}

async function handleUpdateGroup(group, field) {
  rolesSelectorLoading.value.push(group.id);

  const response = await appUserReportUpdateUserGroup({
    idInforme: props.informe.idInforme,
    idGrupo: group.id,
    actualizaArchivos: group.actualizaArchivos,
    gestionaUsuarios: group.gestionaUsuarios,
    roles: group.roles,
    habilitado: group.habilitado,
  });

  rolesSelectorLoading.value = rolesSelectorLoading.value.filter(
    (id) => id != group.id,
  );

  if (response.errors) {
    if (field === "gestionaUsuarios") {
      group[field] = !group[field];
    } else if (field === "Roles") {
      user[field] = [];
    } else if (field === "habilitado") {
      group[field] = !group[field];
    }
  }
}

function deleteGroup(group) {
  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      let response = null;
      const id = originIsReport ? group.id : group.idGrupo;
      deletingGroup.value = id;

      if (originIsReport) {
        response = await userReportDeleteGroup({
          idInforme: props.informe.idInforme,
          idGrupo: id,
        });
      } else {
        response = await appUserGroupDeleteUser({
          email: props.user.email,
          idGrupo: id,
        });
      }

      if (response.status == 200) {
        emit("delete-group");
        onPage();

        toast.add({
          severity: "success",
          summary: "Grupo eliminado",
          detail: "El grupo ha sido eliminado correctamente",
          life: 3000,
        });
      }

      deletingGroup.value = null;
    },
  });
}

async function setTableHeight() {
  const dialog = document.querySelector(
    ".report-has-users-dialog, .user-has-groups-dialog",
  );

  if (!dialog) return;

  await nextTick();

  const dialogHeader = dialog.querySelector(".p-dialog-header");
  const tabsContainer = dialog.querySelector(".p-tabview-nav-container");

  const dialogHeight = dialog.clientHeight;
  const dialogHeaderHeight = dialogHeader.clientHeight;
  const tabsContainerHeight = originIsReport ? tabsContainer.clientHeight : 0;
  const inputGroupHeight = originIsUser
    ? dialog.querySelector(".p-inputgroup").clientHeight
    : 0;
  const customHeight = originIsUser ? "1rem" : 0;

  const maxTableHeight =
    dialogHeight - dialogHeaderHeight - tabsContainerHeight - inputGroupHeight;

  totalHeight.value = `calc(${maxTableHeight}px - 2rem - ${customHeight})`;
}

function getGroupType(grupo) {
  const tipo =
    props.componentOrigin == "user-has-groups" ? grupo.tipoGrupo : grupo.tipo;

  const type = types.find((type) => type.value === tipo);
  return type ? type.label : "Sin grupo";
}

function rowClass(data) {
  return [{ "blink-row": addGroup.value && data.id == addGroup.value.idGrupo }];
}

async function onPage(event = null) {
  loading.value = true;

  if (event) {
    rows.value = event.rows;
    pageIndex.value = event.page + 1;
    pageSize.value = event.rows;
  }

  let sortOrderFormatted = null;

  switch (sortOrder.value) {
    case 1:
      sortOrderFormatted = "asc";
      break;
    case -1:
      sortOrderFormatted = "desc";
      break;
    default:
      sortOrderFormatted = null;
  }

  const filterParam = originIsReport
    ? { alias: props.informe.alias }
    : { email: props.user.email };

  let paginationFunction = null;
  let fields = null;

  if (originIsReport) {
    paginationFunction = appUserReportDetailsGroup;
    fields = ["Titulo"];
  } else {
    paginationFunction = appUserGroupListByUser;
    fields = ["Nombre"];
  }

  const response = await paginationFunction({
    ...filterParam,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    sortField: sortField.value,
    sortOrder: sortOrderFormatted,
    GLOBALSEARCH: {
      VALUE: search.value,
      FIELDS: fields,
    },
  });

  if (response.status == 200) {
    groupsArray.value = response.data.data;
    totalResults.value = response.data.totalResults;
  }

  loading.value = false;
}

function handleSearchGroups(query) {
  search.value = query;
  onPage();
}

function showGroupHasUsersModal(group) {
  userGroupHasUsersModalRef.value.showModal(group);
}

function refreshData() {
  onPage();
}

function openCopyUsersAndGroupsModal(type) {
  emit("copy", type);
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  onPage();
  setTableHeight();

  window.addEventListener("resize", setTableHeight);

  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});

defineExpose({
  refreshData
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.rol-multiselect {
  width: 15rem;

  @media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
    width: 60vw;
  }
}

.overflow-x-auto {
  :deep(.p-datatable-wrapper) {
    overflow: visible !important;
  }
}
</style>
